import { PlusIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { listSitesRequest } from '@/services/sites';
import type { PaginatedResult } from '@/types/roles';
import type { SiteListItem } from '@/types/sites';

const PAGE_SIZE = 20;

type StatusFilter = 'active' | 'inactive' | 'all';

function isValidStatus(s: string | null): s is StatusFilter {
  return s === 'active' || s === 'inactive' || s === 'all';
}

export function SitesListPage(): JSX.Element {
  const { permissions } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQ = searchParams.get('q') ?? '';
  const statusParam = searchParams.get('status');
  const initialStatus: StatusFilter = isValidStatus(statusParam) ? statusParam : 'active';
  const initialPage = (() => {
    const p = Number.parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  })();

  const [q, setQ] = useState<string>(initialQ);
  const [debouncedQ, setDebouncedQ] = useState<string>(initialQ);
  const [status, setStatus] = useState<StatusFilter>(initialStatus);
  const [page, setPage] = useState<number>(initialPage);

  // 300ms debounce for search input.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQ(q);
    }, 300);
    return () => {
      window.clearTimeout(t);
    };
  }, [q]);

  // Sync URL search params when state changes.
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ !== '') next.set('q', debouncedQ);
    if (status !== 'active') next.set('status', status);
    if (page !== 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [debouncedQ, status, page, setSearchParams]);

  // Reset to page 1 when filters change.
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, status]);

  const sitesQuery = useQuery<PaginatedResult<SiteListItem>, ApiError>({
    queryKey: ['sites', debouncedQ, status, page, PAGE_SIZE],
    queryFn: () =>
      listSitesRequest({
        q: debouncedQ,
        status,
        page,
        pageSize: PAGE_SIZE,
      }),
    staleTime: 30_000,
  });

  const columns = useMemo<ColumnDef<SiteListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/sites/${String(row.original.id)}`}
            className="text-primary hover:underline"
            data-testid={`sites-row-name-${String(row.original.id)}`}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'code',
        header: 'Code',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.code}</span>,
      },
      { accessorKey: 'location', header: 'Location' },
      {
        accessorKey: 'siteHeadName',
        header: 'Site Head',
        cell: ({ row }) => row.original.siteHeadName ?? '—',
      },
      {
        accessorKey: 'machinesCount',
        header: 'Machines',
        cell: ({ row }) =>
          row.original.machinesCount === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span>{row.original.machinesCount}</span>
          ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={row.original.isActive ? 'default' : 'secondary'}>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
    ],
    [],
  );

  const data = sitesQuery.data?.items ?? [];
  const total = sitesQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const tableData = data as unknown as SiteListItem[];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('sites.read')) {
    return <Navigate to="/dashboard" replace />;
  }

  const canCreate = permissions.includes('sites.create');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sites</h1>
          <p className="text-muted-foreground">
            Manage industrial sites, their site heads, and operational status.
          </p>
        </div>
        {canCreate && (
          <Button asChild data-testid="sites-add-button">
            <Link to="/sites/new">
              <PlusIcon size={16} weight="bold" /> Add Site
            </Link>
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Sites</CardTitle>
          <CardDescription>
            Search by name or code. Filter by status. Click a row to open its detail view.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px] space-y-1">
              <Label htmlFor="sites-search">Search</Label>
              <Input
                id="sites-search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                }}
                placeholder="Name or code…"
                data-testid="sites-search-input"
              />
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="sites-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => {
                  if (isValidStatus(v)) setStatus(v);
                }}
              >
                <SelectTrigger id="sites-status" data-testid="sites-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active" data-testid="sites-status-active">
                    Active
                  </SelectItem>
                  <SelectItem value="inactive" data-testid="sites-status-inactive">
                    Inactive
                  </SelectItem>
                  <SelectItem value="all" data-testid="sites-status-all">
                    All
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {sitesQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="sites-loading">
              Loading sites…
            </p>
          )}
          {sitesQuery.isError && (
            <p className="text-sm text-destructive" data-testid="sites-error">
              Failed to load sites.
            </p>
          )}
          {sitesQuery.data && (
            <>
              <div className="overflow-x-auto">
              <Table data-testid="sites-table">
                <TableHeader>
                  {table.getHeaderGroups().map((hg) => (
                    <TableRow key={hg.id}>
                      {hg.headers.map((h) => (
                        <TableHead key={h.id}>
                          {h.isPlaceholder
                            ? null
                            : flexRender(h.column.columnDef.header, h.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="text-center py-8">
                        <span data-testid="sites-empty">No sites found.</span>
                      </TableCell>
                    </TableRow>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id} data-testid={`sites-row-${String(row.original.id)}`}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-muted-foreground" data-testid="sites-pagination-summary">
                  Page {page} of {pageCount} • {total} total
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    disabled={page <= 1}
                    data-testid="sites-pagination-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.min(pageCount, p + 1));
                    }}
                    disabled={page >= pageCount}
                    data-testid="sites-pagination-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
